import { api } from '@/lib/api'
import { defaultPaginationMeta } from '@/lib/api/masterCatalog'
import { getTotalPages, paginateItems } from '@/lib/pagination'

/** @typedef {import('@/types/api').PosQuickKey} PosQuickKey */

/**
 * @returns {Promise<PosQuickKey[]>}
 */
export async function fetchPosQuickKeys() {
  const { data } = await api.get('/pos-quick-keys')
  return data.data.quick_keys
}

/**
 * @returns {Promise<PosQuickKey[]>}
 */
export async function fetchMasterPosQuickKeys() {
  const { data } = await api.get('/masters/pos-quick-keys')
  return data.data.quick_keys
}

/**
 * @param {PosQuickKey} key
 * @param {string} q
 */
function matchesQuickKeySearch(key, q) {
  if (!q) return true
  const needle = q.toLowerCase()
  const haystack = [
    key.display_name,
    key.label,
    key.item_type,
    key.service?.name,
    key.product?.name,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
  return haystack.includes(needle)
}

/**
 * @param {PosQuickKey[]} items
 * @param {string} [sortBy]
 * @param {'asc' | 'desc'} [sortDir='asc']
 */
function sortQuickKeys(items, sortBy, sortDir = 'asc') {
  const dir = sortDir === 'desc' ? -1 : 1
  const sorted = [...items]

  if (!sortBy) {
    return sorted.sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
  }

  return sorted.sort((a, b) => {
    const av = a[sortBy] ?? ''
    const bv = b[sortBy] ?? ''
    if (av < bv) return -1 * dir
    if (av > bv) return 1 * dir
    return 0
  })
}

/**
 * @param {Record<string, unknown>} [params]
 * @returns {Promise<{ items: PosQuickKey[], meta: import('@/types/api').PaginationMeta }>}
 */
export async function fetchPosQuickKeysPage(params = {}) {
  const page = Number(params.page ?? 1)
  const perPage = Number(params.per_page ?? 15)
  const q = String(params.q ?? '').trim()
  const sortBy = params.sort_by ? String(params.sort_by) : ''
  const sortDir = params.sort_dir === 'desc' ? 'desc' : 'asc'

  const all = await fetchMasterPosQuickKeys()
  const filtered = all.filter((key) => matchesQuickKeySearch(key, q))
  const sorted = sortQuickKeys(filtered, sortBy || undefined, sortDir)
  const total = sorted.length

  return {
    items: paginateItems(sorted, page, perPage),
    meta: defaultPaginationMeta({
      current_page: page,
      last_page: getTotalPages(total, perPage),
      per_page: perPage,
      total,
    }),
  }
}

/**
 * @param {Record<string, unknown>} [params]
 * @returns {Promise<PosQuickKey[]>}
 */
export async function fetchPosQuickKeysList(params = {}) {
  const q = String(params.q ?? '').trim()
  const sortBy = params.sort_by ? String(params.sort_by) : ''
  const sortDir = params.sort_dir === 'desc' ? 'desc' : 'asc'

  const all = await fetchMasterPosQuickKeys()
  const filtered = all.filter((key) => matchesQuickKeySearch(key, q))
  return sortQuickKeys(filtered, sortBy || undefined, sortDir)
}

/**
 * @param {Partial<PosQuickKey>} payload
 * @returns {Promise<PosQuickKey>}
 */
export async function createPosQuickKey(payload) {
  const { data } = await api.post('/masters/pos-quick-keys', payload)
  return data.data.quick_key
}

/**
 * @param {number} quickKeyId
 * @param {Partial<PosQuickKey>} payload
 * @returns {Promise<PosQuickKey>}
 */
export async function updatePosQuickKey(quickKeyId, payload) {
  const { data } = await api.put(`/masters/pos-quick-keys/${quickKeyId}`, payload)
  return data.data.quick_key
}

/**
 * @param {number} quickKeyId
 */
export async function deletePosQuickKey(quickKeyId) {
  await api.delete(`/masters/pos-quick-keys/${quickKeyId}`)
}

export const POS_QUICK_KEY_ITEM_TYPES = [
  { value: 'service', label: 'Service' },
  { value: 'product', label: 'Product' },
]

export const MAX_POS_QUICK_KEYS = 12
